/**
 * @fileoverview Default ENGLISH strings for `@puffin-core/auth-shell`.
 * @package @puffin-core/auth-shell
 *
 * ## Why this package suddenly has a string bundle (AAS-I18N-002 Phase 7, 2026-08-11)
 *
 * Found by RENDERING, not by a gate. On a Spanish account, pos-web's login page showed correct
 * Spanish body copy — *"Dispositivo no registrado"*, *"Este dispositivo no está registrado en su
 * cuenta"* — wrapped in ENGLISH chrome: *"Skip to main content"* and *"Help"*.
 *
 * The literals lived HERE, in a shared package, and `web/no-hardcoded-strings` censuses `apps/**`.
 * RECIPE **miss-class 9**. This package had no i18n at all — its dependencies were `{ prop-types }` —
 * and it is imported by **FIVE apps** (pos, palm, patrol, planner, prep), so the same English chrome
 * was on all five while four of them scored clean in every source-based instrument.
 *
 * The alternative — threading a `t=` prop from five call sites — is the hand-wired-loader
 * anti-pattern ruling 39 removed once already (20 loaders across 10 apps): one corpus copied five
 * times, and app six forgets. One corpus, one place, inherited by construction.
 *
 * ⚠️ The skip link is a WCAG 2.4.1 bypass control and the help link is the only assistance affordance
 * on the auth screens — these are not decorative chrome.
 */

export const DEFAULT_AUTH_SHELL_STRINGS = Object.freeze({
  'authShell.skipToMainContent': 'Skip to main content',
  'authShell.goToHome': 'Go to home',
  'authShell.getHelp': 'Get help',
  'authShell.helpAndSupport': 'Help & Support',
  'authShell.help': 'Help',
  // WEB-APP-VERSION-REFRESH-001 — <BuildUpdateNotice>: a newer build is ready; the reload waits for a safe moment.
  'authShell.buildUpdateReady': 'A new version is ready.',
  'authShell.buildUpdateRequired': 'This version is out of date. Reload to keep working.',
  'authShell.buildUpdateReloadNow': 'Reload now',
  'authShell.buildUpdateWaitingUnsavedChanges': 'It will update once your changes are saved.',
  'authShell.buildUpdateWaitingSaleInProgress': 'It will update after this sale.',
  'authShell.buildUpdateWaitingSignInInProgress': 'It will update after you sign in.',
  'authShell.buildUpdateWaitingRequestInFlight': 'It will update in a moment.',
  // NUDGE-LINK-001 R-31 / Q-P3F-5 (2026-09-10) — the FOOTER half. AAS-I18N-002 Phase 7 fixed the
  // header's five strings and left `<AuthFooter>` entirely English: the copyright sentence, both
  // legal links, the support link, and the nav's `aria-label` (which a screen reader announces, so
  // it is not decorative). Same miss-class as the original defect, one component over.
  'authShell.allRightsReserved': 'All rights reserved.',
  'authShell.privacyPolicy': 'Privacy Policy',
  'authShell.termsOfService': 'Terms of Service',
  'authShell.support': 'Support',
  'authShell.legalLinks': 'Legal links',
  // L-R7CLOSE row 32, re-keyed by row 39 to the NIST SP 800-63B-4 policy (owner ruling 2026-09-26)
  // — `<PasswordRequirementList>`, the ONE password checklist, and `usePasswordRefusalText`, the ONE
  // refusal sentence. Each `requirement.*` leaf is named by an `@puffin-core/password-policy`
  // requirement KEY, so a requirement added to the policy needs exactly one new leaf here and no
  // component edit. Copy adopted from UMB lane L16's.
  'authShell.password.requirements.label': 'Your password needs',
  'authShell.password.requirement.minLength': 'At least {min} characters',
  'authShell.password.requirement.maxBytes':
    'No longer than {max} bytes (accented letters and emoji count as more than one)',
  'authShell.password.requirement.notCommon': 'Not a common or easily guessed password',
  'authShell.password.requirement.notPersonal':
    'Not built from your name, your email, or the word Puffin',
  'authShell.password.requirement.met': 'done',
  'authShell.password.requirement.notMet': 'not yet',
  'authShell.password.refused': "That password can't be used. It needs: {requirements}.",
  // N139 — `useCredentialRefusalText`, the ONE sentence for a SIGNED-IN person's wrong password or PIN (every
  // API's 403 `CREDENTIAL_INCORRECT`). The PIN leaf names the device because the device-PIN change answers a
  // device that cannot change a PIN with the same refusal as a wrong PIN, on purpose.
  'authShell.credential.incorrect.password': 'That password is incorrect. Try again.',
  'authShell.credential.incorrect.pin': 'That PIN is incorrect for this device. Try again.',
  // S6b — THE password card's own words (every app's card alike), then `usePasswordChangeOutcome`'s refusals and
  // its success.
  'authShell.passwordChange.title': 'Change password',
  'authShell.passwordChange.current': 'Current password',
  'authShell.passwordChange.new': 'New password',
  'authShell.passwordChange.confirm': 'Confirm new password',
  'authShell.passwordChange.submit': 'Change password',
  'authShell.passwordChange.submitting': 'Changing…',
  'authShell.passwordChange.mustDiffer':
    'Your new password must be different from your current one.',
  'authShell.passwordChange.confirmMismatch': 'The two new passwords do not match.',
  'authShell.passwordChange.reused':
    "You've used that password recently. Choose one that isn't your current password or one of the 12 before it.",
  'authShell.passwordChange.unchanged': 'That is the password you are using now. Choose a new one.',
  'authShell.passwordChange.accountInactive':
    'This account is not active, so its password cannot be changed. Contact your manager.',
  'authShell.passwordChange.stepUpRequired':
    'Your business requires two-step verification to change a password. Verify, then try again.',
  // Q-EE-22 "Plain, retry" — the ruled words `@puffin-core/mfa-ui` carries as `error_change_not_recorded`, reused
  // when the password change became a RECORDED change: it is refused 503 `AUDIT_RECORD_UNAVAILABLE`, with nothing
  // changed, when its audit record cannot be kept.
  'authShell.passwordChange.notRecorded':
    'Your change was not made because it could not be recorded. Please try again in a moment.',
  'authShell.passwordChange.tooManyAttempts':
    'Too many attempts. Wait a few minutes, then try again.',
  'authShell.passwordChange.failed': "Your password couldn't be changed. Try again.",
  'authShell.passwordChange.changed': 'Your password has been changed.',
  // GAMING's removal audit E4 — after a change, the offer to end every OTHER session (`<SignOutOtherSessionsOffer>`).
  'authShell.signOutOthers.prompt':
    'Signed in somewhere else, like another browser or phone? You can sign out everywhere but here.',
  'authShell.signOutOthers.action': 'Sign out all other sessions',
  'authShell.signOutOthers.working': 'Signing out…',
  'authShell.signOutOthers.done':
    'Your other sessions are signed out. You are still signed in here.',
  'authShell.signOutOthers.failed': "Your other sessions couldn't be signed out. Try again.",
  // PALM-PHONE-UAT-001 A6 — `<PasswordField>`'s toggle, the ONE pair of words for showing a secret while it is typed
  // (every sign-in, reset, invite and change surface in the fleet; moved out of `passwordChange.*`, and login-shell's
  // and invite-core's copies retired with it).
  'authShell.passwordField.show': 'Show password',
  'authShell.passwordField.hide': 'Hide password',
  // …and the same toggle on a PIN (`kind="pin"`): pos-web's six PIN entries and re-auth's named it
  // from their own catalogs, or not at all.
  'authShell.passwordField.showPin': 'Show PIN',
  'authShell.passwordField.hidePin': 'Hide PIN',
  // PACKAGE-I18N-SEAT (JSDOC-PROP-CONTRACT-001, push 17) — <CompanyContextBanner>, on the first screen
  // of every sign-in: what the visitor is doing at this company. Its logo is decorative (Unit B), so it has no alt text.
  'authShell.companyBanner.login': 'Signing in to',
  'authShell.companyBanner.register': 'Registering for',
  'authShell.companyBanner.device': 'Registering device for',
});

export default DEFAULT_AUTH_SHELL_STRINGS;
